import { ApiResponse, createApiResponse } from "./apiResponse";

function buildUrl(
  host: string,
  path?: string,
  port?: number,
  params?: { [key: string]: string | number | boolean },
): string {
  const portStr = !port || port === 80 || port === 443 ? "" : `:${port}`;
  const filteredParams = params
    ? Object.keys(params).reduce((acc: { [key: string]: string | number | boolean }, key) => {
        const value = params[key];
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {})
    : {};

  const paramStr = Object.keys(filteredParams).length
    ? "?" + Object.keys(filteredParams)
        .map((key) => `${key}=${filteredParams[key]}`)
        .join("&")
    : "";
  return `${host}${portStr}${path || ""}${paramStr}`;
}

async function readResponseBody<T>(response: Response): Promise<T | string | null> {
  try {
    const textSource = typeof response.clone === "function" ? response.clone() : response;
    const rawText = await textSource.text();

    if (!rawText) {
      return null;
    }

    const contentType = response.headers?.get("content-type") || "";
    const trimmedText = rawText.trim();
    const looksLikeJson =
      trimmedText.startsWith("{") ||
      trimmedText.startsWith("[") ||
      trimmedText.startsWith('"') ||
      contentType.includes("application/json");

    if (looksLikeJson) {
      try {
        return JSON.parse(trimmedText) as T;
      } catch {
        return trimmedText as unknown as T;
      }
    }

    return rawText as unknown as T;
  } catch {
    return null;
  }
}

export const apiHelpers = {
  get: async <A>(
    host: string,
    path?: string,
    port?: number,
    params?: { [key: string]: string | number | boolean },
    headers?: { [key: string]: string },
  ): Promise<ApiResponse<A>> => {
    try {
      const url = buildUrl(host, path, port, params);
      const response = await fetch(url, {
        method: "GET",
        headers: headers || {},
      });
      const body = await readResponseBody<A>(response);
      return createApiResponse(response, body as A);
    } catch (err) {
      return createApiResponse<A>(undefined, null, err as Error);
    }
  },

  put: async <A, B>(
    host: string,
    body: A,
    path?: string,
    port?: number,
    params?: { [key: string]: string | number | boolean },
    headers?: { [key: string]: string },
  ): Promise<ApiResponse<B>> => {
    try {
      const url = buildUrl(host, path, port, params);
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(headers || {}),
        },
        body: JSON.stringify(body),
      });
      const responseBody = await readResponseBody<B>(response);
      return createApiResponse(response, responseBody as B);
    } catch (err) {
      return createApiResponse<B>(undefined, null, err as Error);
    }
  },
};
